import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, TrendingUp, Trophy, Calendar, Dumbbell } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (d) => {
  if (!d) return '';
  const parts = String(d).split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
  return d;
};

const fmtLongDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const rpeColor = (rpe) => {
  if (rpe == null) return 'text-gray-400';
  if (rpe <= 5) return 'text-green-400';
  if (rpe <= 7) return 'text-yellow-400';
  if (rpe <= 8) return 'text-orange-400';
  return 'text-red-400';
};

const StatChip = ({ icon, label, value, testid }) => (
  <div className="bg-slate-800/70 border border-slate-700 rounded-xl px-3 py-2 flex flex-col gap-0.5" data-testid={testid}>
    <span className="text-slate-400 text-[11px] uppercase tracking-wide flex items-center gap-1">{icon}{label}</span>
    <span className="text-white text-lg font-bold tabular-nums">{value}</span>
  </div>
);

const LABELS = {
  en: {
    sessions: 'Sessions', prWeight: 'PR Weight', bestE1rm: 'Best e1RM', lastDone: 'Last done',
    progression: 'Progression', breakdown: 'Session breakdown', topSet: 'Top set (kg)',
    est1rm: 'Est. 1RM (kg)', volume: 'Volume (kg)', loading: 'Loading history…',
    error: 'Failed to load history', empty: 'No completed sessions with this exercise yet', vol: 'vol',
  },
  es: {
    sessions: 'Sesiones', prWeight: 'Récord', bestE1rm: 'Mejor 1RM', lastDone: 'Última',
    progression: 'Progresión', breakdown: 'Historial por sesión', topSet: 'Serie top (kg)',
    est1rm: '1RM est. (kg)', volume: 'Volumen (kg)', loading: 'Cargando historial…',
    error: 'No se pudo cargar el historial', empty: 'Aún no hay sesiones completadas con este ejercicio', vol: 'vol',
  },
};

const ExerciseHistoryModal = ({ exerciseId, exerciseName, clientId, clientName, subtitle, lang = 'en', onClose }) => {
  const L = LABELS[lang] || LABELS.en;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/exercises/${exerciseId}/client-history/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setData(res.data);
      } catch (err) {
        if (active) setError(err.response?.data?.detail || L.error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [exerciseId, clientId]);

  const sessions = data?.sessions || [];
  const summary = data?.summary || {};
  const chartData = sessions.map((s) => ({
    date: fmtDate(s.date),
    top_weight: s.top_weight,
    est_1rm: s.best_est_1rm,
    volume: s.total_volume,
  }));

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose}
      data-testid="exercise-history-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-900 border-b border-slate-700 rounded-t-2xl px-5 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <h2 className="text-white text-xl font-bold leading-tight truncate" data-testid="history-exercise-name">
              {data?.exercise_name || exerciseName}
            </h2>
            {(subtitle ?? clientName) && (
              <p className="text-blue-300 text-sm font-medium truncate">{subtitle ?? clientName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-3 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
            data-testid="history-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="py-16 text-center text-slate-400" data-testid="history-loading">{L.loading}</div>
          )}
          {error && !loading && (
            <div className="py-16 text-center text-red-400" data-testid="history-error">{error}</div>
          )}
          {!loading && !error && sessions.length === 0 && (
            <div className="py-16 text-center text-slate-400" data-testid="history-empty">
              {L.empty}.
            </div>
          )}

          {!loading && !error && sessions.length > 0 && (
            <>
              {/* Summary chips */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-testid="history-summary">
                <StatChip icon={<Calendar size={12} />} label={L.sessions} value={summary.times_performed} testid="history-stat-sessions" />
                <StatChip icon={<Trophy size={12} className="text-yellow-400" />} label={L.prWeight} value={`${summary.pr_weight} kg`} testid="history-stat-pr-weight" />
                <StatChip icon={<TrendingUp size={12} className="text-cyan-400" />} label={L.bestE1rm} value={`${summary.pr_est_1rm} kg`} testid="history-stat-pr-1rm" />
                <StatChip icon={<Calendar size={12} />} label={L.lastDone} value={fmtDate(summary.last_performed)} testid="history-stat-last" />
              </div>

              {/* Chart */}
              <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-3 mb-5" data-testid="history-chart">
                <p className="text-slate-300 text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <TrendingUp size={15} className="text-cyan-400" /> {L.progression}
                </p>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" />
                    <YAxis yAxisId="left" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#475569" />
                    <Tooltip
                      contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                      labelStyle={{ color: '#94a3b8' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line yAxisId="left" type="monotone" dataKey="top_weight" name={L.topSet} stroke="#38bdf8" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls isAnimationActive={false} />
                    <Line yAxisId="left" type="monotone" dataKey="est_1rm" name={L.est1rm} stroke="#a78bfa" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2 }} connectNulls isAnimationActive={false} />
                    <Line yAxisId="right" type="monotone" dataKey="volume" name={L.volume} stroke="#34d399" strokeWidth={1.5} dot={false} opacity={0.7} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Session list */}
              <div className="space-y-2" data-testid="history-sessions">
                <p className="text-slate-300 text-sm font-semibold flex items-center gap-1.5 mb-1">
                  <Dumbbell size={15} /> {L.breakdown}
                </p>
                {[...sessions].reverse().map((s) => (
                  <div key={s.workout_id} className="bg-slate-800/40 border border-slate-700 rounded-xl p-3" data-testid={`history-session-${s.workout_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-sm font-semibold">{fmtLongDate(s.date)}</span>
                      <div className="flex items-center gap-1.5">
                        {s.is_weight_pr && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">🏆 PR</span>
                        )}
                        <span className="text-slate-400 text-xs">{s.total_volume} kg {L.vol}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.sets.map((set) => (
                        <div key={set.set_number} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-900/70 border border-slate-700 text-xs">
                          <span className="text-slate-500">#{set.set_number}</span>
                          <span className="font-mono text-white font-semibold">{set.weight}kg × {set.reps}</span>
                          {set.rpe != null && (
                            <span className={`font-bold ${rpeColor(set.rpe)}`}>R{set.rpe}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseHistoryModal;
